/**
 * The refusal screen.
 *
 * Two rules from APP_DEVELOPMENT_GUIDE §7.4, and both are about honesty:
 *
 * 1. **Link out to `apps/store`.** This app never calls store, subscription,
 *    wallet or payment. Its whole relationship with money is one read and some
 *    links.
 * 2. **Surface `entitlement_reason`.** It distinguishes "never assigned" from
 *    "subscription suspended" from "app-mgmt unreachable", and it is what lets
 *    a support advisor answer the customer's actual question.
 *
 * And the thing the copy must never get wrong: **a lapse suppresses; it never
 * revokes** (ADR-0042 §2). The data is still there. Saying otherwise turns a
 * renewal into a panic.
 */

import { Card, buttonClass } from '@fiveox/ui'
import { AlertCircle } from 'lucide-react'

import { refusalCopy } from '../lib/access'
import { CONSOLE_URL, HELP_URL, STORE_URL } from '../lib/config'

interface PaywallOrg {
  id: string
  display_name: string
}

/** A subscription is held by an **organisation**, and this screen renders
 *  outside the shell — so without its own switcher, a member of several orgs
 *  who opened the app under the wrong one is refused with no way to stand in
 *  the right one. Optional, and only offered when there is genuinely somewhere
 *  else to stand: a single-org user gets exactly the screen they had. */
export function Paywall({
  reason,
  orgs,
  selectedOrgId,
  onSelectOrg,
}: {
  reason: string
  orgs?: PaywallOrg[]
  selectedOrgId?: string | null
  onSelectOrg?: (orgId: string) => void
}) {
  const { title, body, cta } = refusalCopy(reason)
  const switchable = onSelectOrg && orgs && orgs.length > 1 ? { orgs, onSelectOrg } : null

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <Card className="w-full max-w-md p-8">
        <AlertCircle className="mb-4 text-muted-foreground" size={28} />
        <h1 className="mb-2 text-lg font-semibold text-foreground">{title}</h1>
        <p className="mb-6 text-sm leading-relaxed text-muted-foreground">{body}</p>

        <div className="flex flex-wrap gap-3">
          {cta === 'store' && (
            <a href={`${STORE_URL}/catalog/gst`} className={buttonClass({ size: 'sm' })}>
              View plans
            </a>
          )}
          {cta === 'console' && (
            <a href={`${CONSOLE_URL}/access`} className={buttonClass({ size: 'sm' })}>
              Manage access
            </a>
          )}
          <a href={HELP_URL} className={buttonClass({ variant: 'outline', size: 'sm' })}>
            Get help
          </a>
        </div>

        {switchable && (
          <div className="mt-6 border-t border-border pt-4">
            <label
              className="mb-1.5 block text-xs font-medium text-muted-foreground"
              htmlFor="paywall-org"
            >
              In the wrong organisation? The subscription may be held by another one.
            </label>
            <select
              id="paywall-org"
              className="gst-select w-full"
              value={selectedOrgId ?? ''}
              onChange={(e) => {
                if (e.target.value && e.target.value !== selectedOrgId) {
                  switchable.onSelectOrg(e.target.value)
                }
              }}
            >
              {switchable.orgs.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.display_name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* The machine reason, shown quietly. An advisor asks for it by name and
            a customer should not have to open a console to find it. */}
        <p className="mt-6 text-xs text-muted-foreground">
          Reference: <code>{reason}</code>
        </p>
      </Card>
    </div>
  )
}
